import { useState, useMemo, useEffect, useRef } from 'react';
import {
  CheckSquare,
  Filter,
  Calendar,
  HelpCircle,
  Star,
  ListTodo,
  Plane,
  Bed,
  Cake,
  PartyPopper,
  Ticket,
  Music,
  Utensils,
  Martini,
  Coffee,
  Binoculars,
  BookmarkPlus,
  ShoppingBag
} from 'lucide-react';
import { getActiveLocationsForDate, getItemColor as defaultGetItemColor } from '../utils';
import { useTags } from '../hooks/useTags';
import { useRecommendations } from '../hooks/useRecommendations';
import { useProject } from '../contexts/ProjectDataContext';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const MONTH_COLORS = [
  'bg-blue-100/80',    // Jan
  'bg-sky-50/80',      // Feb
  'bg-teal-50/80',     // Mar
  'bg-green-50/80',    // Apr
  'bg-lime-50/80',     // May
  'bg-yellow-50/80',   // Jun
  'bg-orange-50/80',   // Jul
  'bg-red-50/80',      // Aug
  'bg-amber-50/80',    // Sep
  'bg-orange-100/80',  // Oct
  'bg-stone-100/80',   // Nov
  'bg-slate-100/80',   // Dec
];

const FILTER_ITEMS = [
  { id: 'nytm-event', label: 'Event', icon: Calendar, color: 'text-blue-500' },
  { id: 'nytm-tentative', label: 'Tentative', icon: HelpCircle, color: 'text-indigo-500' },
  { id: 'nytm-tasks-starred', label: 'Project Starred', icon: Star, color: 'text-yellow-500' },
  { id: 'nytm-tasks-all', label: 'Tasks', icon: ListTodo, color: 'text-green-500' },
  { id: 'nytm-flight', label: 'Flights', icon: Plane, color: 'text-sky-500' },
  { id: 'nytm-hotel', label: 'Hotels', icon: Bed, color: 'text-indigo-500' },
  { id: 'nytm-birthday', label: 'Birthdays', icon: Cake, color: 'text-pink-500' },
  { id: 'nytm-holiday', label: 'Holidays', icon: PartyPopper, color: 'text-violet-600' }
];

const REC_ITEMS = [
  { id: 'nytm-wanttogo', label: 'Want to Go', icon: BookmarkPlus, color: 'text-emerald-500', extraClass: 'mr-2' },
  { id: 'nytm-restaurant', label: 'Food', icon: Utensils, color: 'text-orange-500' },
  { id: 'nytm-drinks', label: 'Drinks', icon: Martini, color: 'text-purple-500' },
  { id: 'nytm-coffee', label: 'Coffee', icon: Coffee, color: 'text-amber-700' },
  { id: 'nytm-sight', label: 'Sights', icon: Binoculars, color: 'text-sky-500' },
  { id: 'nytm-shopping', label: 'Shopping', icon: ShoppingBag, color: 'text-rose-500' },
  { id: 'nytm-musicvenue', label: 'Music Venues', icon: Music, color: 'text-indigo-500' },
  { id: 'nytm-concert', label: 'Concerts', icon: Ticket, color: 'text-pink-500', extraClass: 'ml-2' }
];


const getSundayOfCurrentWeek = () => {
  const today = new Date();
  const day = today.getDay();
  const sunday = new Date(today);
  sunday.setDate(today.getDate() - day);
  sunday.setHours(0, 0, 0, 0);
  return sunday;
};

const isToday = (date) => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const getDistance = (lat1, lon1, lat2, lon2) => {
  if (lat1 === undefined || lat1 === null || lon1 === undefined || lon1 === null || lat2 === undefined || lat2 === null || lon2 === undefined || lon2 === null || isNaN(lat1) || isNaN(lon1) || isNaN(lat2) || isNaN(lon2)) return 9999;
  const R = 3958.8; // Radius of the earth in miles
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export default function MobileCalendarView({
  tasks,
  personalTasks,
  events,
  calendarItems,
  visibleCalendars,
  dispatch,
  navigate,
  profile,
  authUser,
  habits,
  habitLogs,
  handleLogHabit,
  toggleTask,
  getItemColor,
  searchRadius
}) {
  const [weekCount, setWeekCount] = useState(4);
  const [currentStartSunday, setCurrentStartSunday] = useState(getSundayOfCurrentWeek);
  const [displayDate, setDisplayDate] = useState(getSundayOfCurrentWeek());
  const { recommendations } = useRecommendations();
  const { songkickConcerts } = useProject();
  const { getParentTags } = useTags();

  const [showRecFilters, setShowRecFilters] = useState(false);

  const longPressTimerRef = useRef(null);
  const scrollRef = useRef(null);
  const lastWeekCountRef = useRef(weekCount);

  // Anchor scroll position when weekCount changes
  useEffect(() => {
    if (scrollRef.current && lastWeekCountRef.current !== weekCount) {
      const container = scrollRef.current;
      const oldWeekHeight = container.offsetHeight / lastWeekCountRef.current;
      const newWeekHeight = container.offsetHeight / weekCount;
      const currentWeekIndex = container.scrollTop / oldWeekHeight;
      
      container.scrollTop = currentWeekIndex * newWeekHeight;
      lastWeekCountRef.current = weekCount;
    }
  }, [weekCount]);

  const onScroll = (e) => {
    const container = e.target;
    const scrollPos = container.scrollTop;
    const weekHeight = container.offsetHeight / weekCount;
    const visibleWeekIndex = Math.round(scrollPos / weekHeight);
    
    // Find the date of the week at the top
    const weekAtTop = weeksGrid[visibleWeekIndex];
    if (weekAtTop && weekAtTop[0]) {
      // If any day in this week is the 1st of a month, we might want to prioritize that month?
      // Prompt says: "the month changes when we scroll to the first week that has the 1st of the next month"
      const firstOfMonth = weekAtTop.find(d => d.getDate() === 1);
      if (firstOfMonth) {
        setDisplayDate(firstOfMonth);
      } else {
        setDisplayDate(weekAtTop[0]);
      }
    }
  };

  const handleLongPressAction = async (id) => {
    const input = window.prompt(`Create ${id === 'nytm-tentative' ? 'Tentative Event' : 'Event'} using Natural Language (e.g. "Dinner at Mario's on Friday at 7pm"):`);
    if (!input) return;

    const type = id === 'nytm-tentative' ? 'tentative' : 'event';
    const now = new Date();
    const todayId = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    
    dispatch({ 
      type: 'OPEN_ADD_EVENT', 
      payload: { type, title: input, date: todayId } 
    });

    const chrono = await import('chrono-node');
    const refDate = new Date(todayId + 'T12:00:00');
    const parsed = chrono.casual.parse(input, refDate);
    
    if (parsed && parsed.length > 0) {
      const result = parsed[0];
      const start = result.start;
      const end = result.end;
      
      const currentHour = now.getHours();
      if (start && start.isCertain('hour') && !start.isCertain('meridiem')) {
        const h = start.get('hour');
        if (h < 12 && h < currentHour) {
          start.assign('meridiem', 1);
          start.assign('hour', h + 12);
        }
      }
      if (end && end.isCertain('hour') && !end.isCertain('meridiem')) {
        const h = end.get('hour');
        const compareHour = start && start.isCertain('hour') ? start.get('hour') : currentHour;
        if (h < 12 && h < compareHour) {
          end.assign('meridiem', 1);
          end.assign('hour', h + 12);
        }
      }
      
      const updates = {};
      if (start) {
        const sDate = start.date();
        const yyyy = sDate.getFullYear();
        const mm = String(sDate.getMonth() + 1).padStart(2, '0');
        const dd = String(sDate.getDate()).padStart(2, '0');
        updates.newEventStartDate = `${yyyy}-${mm}-${dd}`;
        if (start.isCertain('hour')) {
          updates.newEventStartTime = sDate.toTimeString().substring(0, 5);
          updates.newEventIsAllDay = false;
        }
      }
      if (end) {
        const eDate = end.date();
        const yyyy = eDate.getFullYear();
        const mm = String(eDate.getMonth() + 1).padStart(2, '0');
        const dd = String(eDate.getDate()).padStart(2, '0');
        updates.newEventEndDate = `${yyyy}-${mm}-${dd}`;
        if (end.isCertain('hour')) {
          updates.newEventEndTime = eDate.toTimeString().substring(0, 5);
        }
      } else if (start) {
        updates.newEventEndDate = updates.newEventStartDate;
        if (start.isCertain('hour')) {
          const sDate = start.date();
          const defaultEndDate = new Date(sDate.getTime() + 60 * 60 * 1000);
          const yyyy = defaultEndDate.getFullYear();
          const mm = String(defaultEndDate.getMonth() + 1).padStart(2, '0');
          const dd = String(defaultEndDate.getDate()).padStart(2, '0');
          updates.newEventEndDate = `${yyyy}-${mm}-${dd}`;
          updates.newEventEndTime = defaultEndDate.toTimeString().substring(0, 5);
        }
      }
      
      let newTitle = input.replace(result.text, '');
      newTitle = newTitle.replace(/(?:^|\s)(at|on|@)\s*$/i, '');
      newTitle = newTitle.replace(/^\s*(at|on|@)(?:\s|$)/i, '');
      newTitle = newTitle.replace(/\s+/g, ' ').trim();
      newTitle = newTitle.replace(/\b\w/g, c => c.toUpperCase());
      if (!newTitle) newTitle = 'New Event';
      
      updates.newEventTitle = newTitle;
      
      dispatch({ type: 'SET_NEW_EVENT_INPUT', payload: updates });
    }
  };

  const handleTouchStart = (id) => {
    if (id !== 'nytm-event' && id !== 'nytm-tentative') return;
    longPressTimerRef.current = setTimeout(() => {
      handleLongPressAction(id);
    }, 800);
  };

  const handleTouchEndOrMove = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('clearFilters') === 'true') {
      dispatch({ type: 'SET_CALENDAR_VISIBILITY', payload: null });
    }
  }, [dispatch]);

  const colorGetter = getItemColor || defaultGetItemColor;
  const visibleList = useMemo(() => {
    return Array.isArray(visibleCalendars)
      ? visibleCalendars
      : [
          'nytm-event',
          'nytm-tasks-all',
          'nytm-tasks-starred',
          'nytm-tasks-h',
          'nytm-tasks-m',
          'nytm-tasks-l',
          'nytm-birthday',
          'nytm-holiday'
        ];
  }, [visibleCalendars]);

  const toggleCalendar = (id) => {
    let newList = [...visibleList];
    if (newList.includes(id)) {
      newList = newList.filter((x) => x !== id);
    } else {
      newList.push(id);
    }
    dispatch({ type: 'SET_CALENDAR_VISIBILITY', payload: newList });
  };

  const navigateWeeks = (n) => {
    setCurrentStartSunday((prev) => {
      const next = new Date(prev);
      next.setDate(prev.getDate() + n * 7);
      return next;
    });
  };

  const weeksGrid = useMemo(() => {
    const grid = [];
    // Start 26 weeks before the current Sunday to give plenty of back-scroll
    const start = new Date(currentStartSunday);
    start.setDate(start.getDate() - 7 * 26);
    
    for (let w = 0; w < 52; w++) {
      const week = [];
      for (let d = 0; d < 7; d++) {
        const date = new Date(start);
        date.setDate(start.getDate() + (w * 7 + d));
        week.push(date);
      }
      grid.push(week);
    }
    return grid;
  }, [currentStartSunday]);

  // Scroll to current week on mount
  useEffect(() => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const weekHeight = container.offsetHeight / weekCount;
      // Index 26 is our "currentStartSunday" (since we go 26 weeks back)
      container.scrollTop = 26 * weekHeight;
    }
  }, []); // Only on mount

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  const headerMonthYear = useMemo(() => {
    return `${monthNames[displayDate.getMonth()]} ${displayDate.getFullYear()}`;
  }, [displayDate]);

  const headerBgColor = useMemo(() => {
    return MONTH_COLORS[displayDate.getMonth()];
  }, [displayDate]);

  const getRecommendationsForDay = (day, dayId) => {
    const showFood = visibleList.includes('nytm-restaurant');
    const showDrinks = visibleList.includes('nytm-drinks');
    const showCoffee = visibleList.includes('nytm-coffee');
    const showSights = visibleList.includes('nytm-sight');
    const showShopping = visibleList.includes('nytm-shopping');
    const showConcerts = visibleList.includes('nytm-concert');
    const showMusicVenues = visibleList.includes('nytm-musicvenue');
    const showWantToGo = visibleList.includes('nytm-wanttogo');
    const showAnyRecs = showFood || showDrinks || showCoffee || showSights || showShopping || showMusicVenues || showWantToGo || showConcerts;

    if (!showAnyRecs) return [];

    const homeCity = profile?.cities?.[0] || {
      name: 'Home',
      lat: 38.9072,
      lng: -77.0369,
      timezone: 'America/New_York'
    };

    const dayTrips = (calendarItems || []).filter((item) => item.type === 'trip');
    const dayEvents = (calendarItems || []).filter((item) => item.type === 'event');

    const locsForDay = getActiveLocationsForDate(dayId, day, dayTrips, dayEvents, homeCity);

    const filteredRecs = (recommendations || []).filter((rec) => {
      const isFood = rec.tags?.some((t) => getParentTags(t).includes('Food') || t === 'Food');
      const isDrinks = rec.tags?.some((t) => getParentTags(t).includes('Drinks') || t === 'Drinks');
      const isCoffee = rec.tags?.some((t) => getParentTags(t).includes('Coffee') || t === 'Coffee');
      const isSight = rec.tags?.some(
        (t) =>
          getParentTags(t).includes('Sights') ||
          t === 'Sights' ||
          getParentTags(t).includes('Sight') ||
          t === 'Sight'
      );
      const isMusicVenue = rec.tags?.some((t) => getParentTags(t).includes('Music Venue') || t === 'Music Venue');
      const isShopping = rec.tags?.some((t) => getParentTags(t).includes('Shopping') || t === 'Shopping');
      const isWantToGo = rec.tags?.includes('Want to Go');

      const anyPrimarySelected = showFood || showDrinks || showCoffee || showSights || showShopping || showMusicVenues;

      // If "Want to Go" is ON, but no primary categories are ON, it shows NOTHING.
      if (showWantToGo && !anyPrimarySelected) return false;

      let matchesPrimary = false;
      if (showFood && isFood) matchesPrimary = true;
      if (showDrinks && isDrinks) matchesPrimary = true;
      if (showCoffee && isCoffee) matchesPrimary = true;
      if (showSights && isSight) matchesPrimary = true;
      if (showShopping && isShopping) matchesPrimary = true;
      if (showMusicVenues && isMusicVenue) matchesPrimary = true;

      if (anyPrimarySelected) {
          if (!matchesPrimary) return false; // Must match at least one selected primary category
          if (showWantToGo && !isWantToGo) return false; // Intersection filter
      } else {
          return false; // No primary categories selected and showWantToGo is false
      }

      if (rec.weekdayDescriptions && rec.weekdayDescriptions.length === 7) {
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayName = dayNames[day.getDay()];
        const lowerDayName = dayName.toLowerCase();
        const lowerAbbr = lowerDayName.substring(0, 3);
        const desc = rec.weekdayDescriptions.find((d) => {
          const lowerD = d.toLowerCase().trim();
          return lowerD.includes(lowerDayName) || lowerD.includes(lowerAbbr);
        });
        if (desc) {
          const lowerDesc = desc.toLowerCase().trim();
          if (lowerDesc.includes('closed') || lowerDesc === lowerDayName || lowerDesc === lowerAbbr) {
            return false;
          }
        }
      }

      return true;
    });

    const groupedRecs = {};
    filteredRecs.forEach((rec) => {
      if (!groupedRecs[rec.placeId]) {
        groupedRecs[rec.placeId] = { ...rec, users: new Set([rec.userId]) };
      } else {
        groupedRecs[rec.placeId].users.add(rec.userId);
      }
    });

    const finalRecs = [];
    Object.values(groupedRecs).forEach((rec) => {
      let matchLoc = null;
      let minDistance = Infinity;

      if (locsForDay.length > 0) {
        for (const loc of locsForDay) {
          const targetLat = rec.addressLat || rec.lat;
          const targetLng = rec.addressLng || rec.lng;
          if (!targetLat || !targetLng) continue;
          
          const d = getDistance(loc.lat, loc.lng, targetLat, targetLng);
          if (d <= Number(searchRadius ?? 50) && d < minDistance) {
            matchLoc = loc;
            minDistance = d;
          }
        }
      }

      if (matchLoc) {
        let titleStr = rec.placeName;
        if (rec.weekdayDescriptions && rec.weekdayDescriptions.length === 7) {
          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const dayName = dayNames[day.getDay()];
          const lowerDayName = dayName.toLowerCase();
          const lowerAbbr = lowerDayName.substring(0, 3);
          const desc = rec.weekdayDescriptions.find((d) => {
            const lowerD = d.toLowerCase().trim();
            return lowerD.includes(lowerDayName) || lowerD.includes(lowerAbbr);
          });
          if (desc) {
            const lowerDesc = desc.toLowerCase().trim();
            const isClosed = lowerDesc.includes('closed') || lowerDesc === lowerDayName || lowerDesc === lowerAbbr;
            if (!isClosed) {
              const timePart = desc.substring(desc.indexOf(':') + 1).trim();
              if (timePart) {
                const timeFormatted = timePart
                  .replace(/:00/g, '')
                  .replace(/\s/g, '')
                  .replace(/AM/gi, 'a')
                  .replace(/PM/gi, 'p');
                titleStr = `${rec.placeName} ${timeFormatted}`;
              }
            }
          }
        }
        if (minDistance !== Infinity) {
          titleStr = `${titleStr} (${minDistance.toFixed(1)} mi)`;
        }

        const isFood = rec.tags?.some((t) => getParentTags(t).includes('Food') || t === 'Food');
        const isDrinks = rec.tags?.some((t) => getParentTags(t).includes('Drinks') || t === 'Drinks');
        const isCoffee = rec.tags?.some((t) => getParentTags(t).includes('Coffee') || t === 'Coffee');
        const isSight = rec.tags?.some(
          (t) =>
            getParentTags(t).includes('Sights') ||
            t === 'Sights' ||
            getParentTags(t).includes('Sight') ||
            t === 'Sight'
        );
        const isMusicVenue = rec.tags?.some((t) => getParentTags(t).includes('Music Venue') || t === 'Music Venue');
        const isShopping = rec.tags?.some((t) => getParentTags(t).includes('Shopping') || t === 'Shopping');
        const isWantToGo = rec.tags?.some((t) => typeof t === 'string' && t.toLowerCase() === 'want to go');

        const activeTypes = [];
        if (showFood && isFood) activeTypes.push('restaurant');
        if (showDrinks && isDrinks) activeTypes.push('drinks');
        if (showCoffee && isCoffee) activeTypes.push('coffee');
        if (showSights && isSight) activeTypes.push('sight');
        if (showShopping && isShopping) activeTypes.push('shopping');
        if (showMusicVenues && isMusicVenue) activeTypes.push('concert');
        
        let recType = activeTypes.length > 0 ? activeTypes[0] : 'wanttogo';
        const hasWantToGoOutline = isWantToGo;

        finalRecs.push({
          id: `rec-${rec.placeId}-${dayId}`,
          type: recType,
          multiTypes: activeTypes,
          hasWantToGoOutline: hasWantToGoOutline,
          title: titleStr,
          startDate: dayId,
          endDate: dayId,
          city: matchLoc.city,
          timezoneSource: matchLoc.tz,
          isRecommendation: true,
          isReadOnly: true,
          note: rec.note,
          data: rec,
          minDistance: minDistance
        });
      }
    });

    if (showConcerts && songkickConcerts?.length > 0) {
      songkickConcerts.forEach((concert) => {
        if (concert.isCanceled || !concert.lat || !concert.lng) return;
        if (concert.startDate !== dayId) return;

        let matchLoc = null;
        let minDistance = Infinity;

        if (locsForDay.length > 0) {
          for (const loc of locsForDay) {
            const d = getDistance(loc.lat, loc.lng, concert.lat, concert.lng);
            if (d <= Number(searchRadius ?? 50) && d < minDistance) {
              matchLoc = loc;
              minDistance = d;
            }
          }
        }

        if (matchLoc) {
          finalRecs.push({
            id: concert.id,
            type: 'concert',
            multiTypes: ['concert'],
            hasWantToGoOutline: false,
            title: `${concert.title || concert.artist} (${minDistance.toFixed(1)} mi)`,
            startDate: dayId,
            endDate: dayId,
            city: matchLoc.city,
            timezoneSource: matchLoc.tz,
            isRecommendation: true,
            isReadOnly: true,
            data: concert,
            minDistance: minDistance
          });
        }
      });
    }

    return finalRecs;
  };

  const getDayLabel = (day) => {
    if (day.getDate() === 1) {
      const shortMonth = day.toLocaleDateString('en-US', { month: 'short' });
      return `${shortMonth} 1`;
    }
    return day.getDate().toString();
  };

  return (
    <div data-testid="mobile-calendar-view" className="w-full h-full flex flex-col bg-white overflow-hidden select-none">
      {/* Top Header */}
      <header
        data-testid="mobile-calendar-header" 
        className={`flex items-center px-4 py-2 border-b border-zinc-200 transition-colors duration-500 shrink-0 gap-3 ${headerBgColor}`}
      >
        <button
          data-testid="mobile-today-btn"
          onClick={() => {
            if (scrollRef.current) {
              const container = scrollRef.current;
              const weekHeight = container.offsetHeight / weekCount;
              container.scrollTop = 26 * weekHeight;
            }
          }}
          className="px-3 py-1 bg-white/60 hover:bg-white/80 text-zinc-800 text-sm font-bold rounded-lg transition-colors border border-zinc-300/50"
        >
          Today
        </button>
        <div className="flex-1 flex flex-col items-center ml-1">
           <input 
             type="range" 
             min="1" max="8" step="1" 
             value={weekCount} 
             onChange={(e) => setWeekCount(Number(e.target.value))}
             className="w-32 h-1 bg-zinc-300/50 rounded-lg appearance-none cursor-pointer accent-zinc-800"
             title="Visible Weeks"
           />
        </div>

        <span data-testid="calendar-header-month" className="text-lg font-bold text-zinc-900 text-right">
          {headerMonthYear.toUpperCase()}
        </span>
      </header>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 text-center py-1 border-b border-zinc-200 bg-zinc-50/50 shrink-0">
        {WEEKDAYS.map((dayName) => (
          <div key={dayName} className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">
            {dayName}
          </div>
        ))}
      </div>

      {/* Main Grid */}
      <div 
        ref={scrollRef}
        onScroll={onScroll}
        data-testid="mobile-calendar-grid" 
        className="flex-1 overflow-y-auto scrollbar-none flex flex-col bg-zinc-200 gap-[1px]"
      >
        {weeksGrid.map((week, wIndex) => (
          <div 
            key={wIndex} 
            data-testid={`mobile-grid-week-${wIndex}`} 
            className="flex shrink-0 bg-white gap-[1px]"
            style={{ height: `calc(100% / ${weekCount})` }}
          >
            {week.map((day, dIndex) => {
              const dayId = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(
                day.getDate()
              ).padStart(2, '0')}`;
              const isCurrentMonth = day.getMonth() === displayDate.getMonth();
              const isTodayDay = isToday(day);

              // Get day events and tasks (include trip items for the red strip)
              const dayRecs = getRecommendationsForDay(day, dayId);
              const dayTrips = (calendarItems || []).filter(item => item.type === 'trip' && item.startDate <= dayId && item.endDate >= dayId);
              const dayItems = [
                ...(calendarItems || []).filter(
                  (item) => {
                    if (item.type === 'trip') return false;
                    if (item.startDate > dayId || item.endDate < dayId) return false;
                    if (item.type === 'task') {
                      if (item.isProjectStarred) {
                        return visibleList.includes('nytm-tasks-starred');
                      }
                      return visibleList.includes('nytm-tasks-all');
                    }
                    return true;
                  }
                ),
                ...dayRecs
              ];

              // Habits logic
              const monthKey = dayId.substring(0, 7);
              const uid = authUser?.uid || 'test-uid-123';
              const docId = `${uid}_${monthKey}`;
              const logDoc = (habitLogs || []).find((l) => l.id === docId);
              const dayLog = logDoc?.days?.[dayId] || {};
              const activeHabits = (habits || []).filter((habit) => {
                const start = habit.startDate;
                const end = habit.endDate || '9999-12-31';
                return dayId >= start && dayId <= end;
              });

              return (
                <div
                  key={dIndex}
                  data-testid={`mobile-day-cell-${dayId}`}
                  data-today={isTodayDay ? 'true' : 'false'}
                  data-inactive={isCurrentMonth ? 'false' : 'true'}
                  onClick={() => {
                    dispatch({ 
                      type: 'OPEN_ADD_EVENT', 
                      payload: { type: 'event', title: '', date: dayId } 
                    });
                  }}
                  className={`w-[calc(100%/7)] shrink-0 flex flex-col min-h-0 relative transition-colors border-r border-zinc-100 last:border-r-0 overflow-hidden ${
                    isCurrentMonth ? 'bg-white' : 'bg-zinc-50/50'
                  }`}
                >
                  {/* Day number */}
                  <div className="flex items-center justify-between shrink-0 mb-0.5 p-1">
                    <span
                      className={`text-[10px] px-1 py-0.5 rounded ${
                        isTodayDay
                          ? 'font-bold bg-blue-100 text-blue-700'
                          : isCurrentMonth
                          ? 'font-bold text-zinc-800'
                          : 'font-normal text-zinc-400 opacity-60'
                      }`}
                    >
                      {getDayLabel(day)}
                    </span>
                  </div>

                  {/* Day Content Area */}
                  <div className="flex-1 overflow-y-auto scrollbar-none flex flex-col gap-px min-h-0">
                    {/* Render Trips (Red Strip) */}
                    {dayTrips.map(trip => (
                      <div 
                        key={trip.id} 
                        className="bg-red-500 text-white text-[9px] font-bold px-1 py-0.5 mb-0.5 truncate leading-tight"
                        title={trip.title}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {trip.city || trip.title}
                      </div>
                    ))}

                    {/* Render Events / Tasks */}
                    {dayItems.map((item) => {
                      if (item.type === 'task') {
                        const isHeader = item.isHeader || (item.title || '').trim().endsWith(':');
                        if (isHeader) {
                          return (
                            <div key={item.id} className="text-[9px] font-bold text-zinc-600 truncate px-1">
                              {item.title}
                            </div>
                          );
                        }

                        const completed = !!item.completed;
                        return (
                          <div
                            key={item.id}
                            className="flex items-center gap-1 text-[9px] px-1 py-px truncate font-semibold text-zinc-900"
                            style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Open task edit or just toggle
                            }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleTask?.(item.originalId || item.id, completed);
                              }}
                              className={`shrink-0 ${completed ? 'text-green-600' : 'text-zinc-400'}`}
                            >
                              {completed ? (
                                <CheckSquare size={10} />
                              ) : (
                                <div className="w-2.5 h-2.5 border border-zinc-400 rounded-sm bg-white" />
                              )}
                            </button>
                            <span className={`truncate ${completed ? 'line-through opacity-50' : ''}`}>
                              {item.title}
                            </span>
                          </div>
                        );
                      }

                      // Non-task items (Events, Recommendations)
                      const isTimed = item.startTime && !item.isAllDay;
                      const bgColor = isTimed ? 'transparent' : colorGetter(item);
                      
                      let bgStyle = { backgroundColor: bgColor };
                      if (!isTimed && item.multiTypes && item.multiTypes.length > 1) {
                        const stops = [];
                        const len = item.multiTypes.length;
                        item.multiTypes.forEach((t, i) => {
                          const color = colorGetter({ type: t });
                          const start = (i / len) * 100;
                          const end = ((i + 1) / len) * 100;
                          stops.push(`${color} ${start}%`);
                          stops.push(`${color} ${end}%`);
                        });
                        bgStyle = { background: `linear-gradient(to right, ${stops.join(', ')})` };
                      }
                      
                      const textColor = isTimed ? colorGetter(item) : 'white';

                      return (
                        <div
                          key={item.id}
                          className={`text-[9px] px-1 py-px truncate font-medium ${isTimed ? '' : 'text-white'} ${item.hasWantToGoOutline ? 'ring-1 ring-emerald-500 ring-inset' : ''}`}
                          style={{ 
                            ...bgStyle,
                            color: textColor,
                            fontWeight: 500
                          }}
                          title={item.title}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {item.title}
                        </div>
                      );
                    })}

                    {/* Render Habits */}
                    {activeHabits.map((habit) => {
                        const entry = dayLog[habit.id] || {};
                        const completed = !!entry.completed;
                        return (
                          <div
                            key={habit.id}
                            className="flex items-center gap-1 text-[9px] px-1 py-px truncate bg-zinc-50 border-t border-b border-zinc-100 font-semibold"
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleLogHabit?.(monthKey, dayId, habit.id, { ...entry, completed: !completed }, true);
                              }}
                              className={`shrink-0 transition-colors ${
                                completed ? 'text-green-600' : 'text-zinc-300 hover:text-zinc-500'
                              }`}
                            >
                              {completed ? (
                                <CheckSquare size={10} />
                              ) : (
                                <div className="w-2.5 h-2.5 border border-zinc-300 rounded-sm bg-white" />
                              )}
                            </button>
                            <span className={`truncate text-zinc-700 ${completed ? 'line-through opacity-50' : ''}`}>
                              {habit.title}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Bottom filter bar */}
      <div className="flex flex-col w-full shrink-0 relative z-20">
        {showRecFilters && (
          <div className="flex items-center gap-1 px-2 py-1 bg-zinc-50 border-t border-zinc-200 overflow-x-auto scrollbar-none flex-nowrap animate-in slide-in-from-bottom-2">
            {REC_ITEMS.map((item) => {
              const isSelected = visibleList.includes(item.id);
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  data-active={isSelected ? 'true' : 'false'}
                  onClick={() => toggleCalendar(item.id)}
                  onTouchStart={() => handleTouchStart(item.id)}
                  onTouchEnd={handleTouchEndOrMove}
                  onTouchMove={handleTouchEndOrMove}
                  className={`flex flex-col items-center justify-center p-1.5 rounded-lg transition-all border border-transparent bg-transparent shrink-0 ${item.extraClass || ''}`}
                  title={item.label}
                >
                  <Icon size={18} className={isSelected ? item.color : 'text-zinc-300'} />
                </button>
              );
            })}
          </div>
        )}
        <div
          data-testid="bottom-filter-bar"
          className="flex items-center justify-between px-2 py-1.5 bg-zinc-50 border-t border-zinc-200 w-full select-none"
        >
          {FILTER_ITEMS.map((item) => {
            const isSelected = visibleList.includes(item.id);
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                data-testid={`filter-button-${item.label}`}
                data-active={isSelected ? 'true' : 'false'}
                onClick={() => toggleCalendar(item.id)}
                onTouchStart={() => handleTouchStart(item.id)}
                onTouchEnd={handleTouchEndOrMove}
                onTouchMove={handleTouchEndOrMove}
                onContextMenu={(e) => {
                  if (item.id === 'nytm-event' || item.id === 'nytm-tentative') {
                    e.preventDefault();
                  }
                }}
                className="flex flex-col items-center justify-center p-1.5 rounded-lg transition-all border border-transparent bg-transparent"
                title={item.label}
              >
                <Icon size={18} className={isSelected ? item.color : 'text-zinc-300'} />
              </button>
            );
          })}
          
          <div className="w-px h-5 bg-zinc-300 mx-0.5 shrink-0" />
          
          <button
            onClick={() => setShowRecFilters(!showRecFilters)}
            className={`flex flex-col items-center justify-center p-1.5 rounded-lg transition-all shrink-0 ${showRecFilters ? 'bg-zinc-200' : 'bg-transparent'}`}
          >
            <Filter size={18} className={showRecFilters ? 'text-zinc-800' : 'text-zinc-400'} />
          </button>
        </div>
      </div>
    </div>
  );
}
